import { environment } from '../../../../environments/environment';
import { catchError, concatMap, EMPTY, filter, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { ILegacyPaginatedComments } from '../interfaces/ILegacyPaginatedComments';
import { inject, Injectable } from '@angular/core';
import { IRequestComment } from '../interfaces/IRequestComment';
import { AbstractCommentService } from './abstract-comment.service';
import { PartialComment } from '../types/PartialComment';
import { AbstractAttachmentService } from './abstract-attachment.service';
import { NamAttachmentService } from '../../../nam/services/attachment.service';
import { EFileEntity } from '@shs-digi-care-monorepo/interfaces';
import { Paginated, PageMetadataWithCursor } from '../models/paginated.model';
import { RequestComment } from '../models/comment.model';
import { UploadFile } from '../interfaces/uploadFile';

@Injectable({
    providedIn: 'root'
})
export class NamCommentService extends AbstractCommentService {
    protected override attachmentService: AbstractAttachmentService = inject(NamAttachmentService);
    /**
     * @param {string} requestId - id of request the comments belong to
     * @param {string} [cursor] cursor for pagination
     * @param {number} [pageSize=10] maximum number of request per page.
     */
    public getComments(
        requestId: string,
        params: { page?: number; pageSize?: number; cursor?: string }
    ): Observable<ILegacyPaginatedComments> {
        const queryParams: Record<string, string> = {
            limit: params.pageSize?.toString() ?? '10'
        };

        if (params.cursor) {
            queryParams['cursor'] = params.cursor;
        }

        return this.httpService
            .get<Paginated<RequestComment, PageMetadataWithCursor>>(
                `${environment.apiUrl}/requests/${requestId}/comments`,
                {
                    params: queryParams
                }
            )
            .pipe(
                map((response) => this.mapCursorPaginationToLegacy(response)),
                catchError((e) => {
                    console.error(e);
                    this.snackbarService.openSnackbar({
                        type: 'Error',
                        title: 'Comments',
                        desc: 'Failed to load comments.',
                        error: e
                    });
                    return EMPTY;
                })
            );
    }

    /**
     * Maps cursor-based pagination response to legacy pagination format.
     * @param {Paginated<RequestComment, PageMetadataWithCursor>} response - The paginated comments from comment-service.
     * @returns {ILegacyPaginatedComments} - The mapped comments in the legacy format.
     */
    private mapCursorPaginationToLegacy(
        response: Paginated<RequestComment, PageMetadataWithCursor>
    ): ILegacyPaginatedComments {
        const mappedComments = response.items.map((comment) => ({
            ...comment,
            requestId: comment.correlationId
        })) as IRequestComment[];

        return {
            data: mappedComments,
            page: 0, // Page number is not relevant for cursor-based pagination
            pageSize: response.items.length,
            total: response.items.length, // Total is not available in cursor-based pagination
            cursor: response.metadata.cursor || undefined
        };
    }

    public createComment(
        requestId: string,
        comment: PartialComment,
        internal: boolean,
        notifyResponsible?: boolean
    ): Observable<IRequestComment> {
        const { body, attachments, referralId } = comment;
        const requestBody: {
            body: string;
            internal: boolean;
            notifyResponsible: boolean;
            files?: string[];
            referralId?: string | null;
        } = {
            body: body,
            internal: internal,
            notifyResponsible: notifyResponsible ?? false,
            ...(referralId ? { referralId } : {})
        };
        return this.handleCommentAttachmentUploads(attachments, requestId).pipe(
            concatMap((files) => {
                if (files.length > 0) {
                    requestBody.files = files;
                }
                return this.httpService.post<IRequestComment>(
                    `${environment.apiUrl}/requests/${requestId}/comments`,
                    requestBody
                );
            }),
            catchError((e) => {
                console.error(e);
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: 'Comments',
                    desc: 'Failed to create comment.',
                    error: e
                });
                throw e; // Rethrow the error to propagate it to the caller
            })
        );
    }

    public handleCommentAttachmentUploads(
        attachments: UploadFile[] | undefined,
        requestId: string
    ): Observable<string[]> {
        return !!attachments && attachments.length > 0
            ? from(attachments).pipe(
                  mergeMap((attachment) =>
                      this.attachmentService
                          .uploadFile({
                              file: attachment,
                              requestId,
                              fileEntity: EFileEntity.Request
                          })
                          .pipe(map(() => attachment?.name || undefined))
                  ),
                  catchError((e) => {
                      console.error(e);
                      this.snackbarService.openSnackbar({
                          type: 'Error',
                          title: 'Comments',
                          desc: 'Failed to upload attachment.',
                          error: e
                      });
                      throw e; // Rethrow the error to propagate it to the caller
                  }),
                  filter((name): name is string => !!name),
                  toArray()
              )
            : of([]);
    }

    public deleteComment(requestId: string, commentId: string): Observable<void> {
        return this.httpService.delete<void>(`${environment.apiUrl}/requests/${requestId}/comments/${commentId}`).pipe(
            catchError((e) => {
                console.error(e);
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: 'Comments',
                    desc: 'Failed to delete comment.',
                    error: e
                });
                return EMPTY;
            })
        );
    }
}
